/**
 * Google Analytics Service
 * Bietet eine typisierte Schnittstelle für Google Analytics Events
 * mit DSGVO-konformem Consent Management
 */

import logger from '../utils/logger';
import type { NudgePromotionId } from '../config/nudgePromotions';

/**
 * GA4 Measurement-ID (Marketing UND App teilen eine Property).
 * Zentral hier statt inline verstreut (Bestands-Bug: hartkodierte ID in
 * trackPageView). Exportiert, damit index.html-Config und Tests eine Quelle haben.
 */
export const GA_MEASUREMENT_ID = 'G-YDZCCRPF7M';

/** Obergrenze der In-Memory-Pre-Consent-Queue (FIFO, ältester wird verdrängt). */
const MAX_PENDING_EVENTS = 50;

/** localStorage-Key für das explizite Opt-out (Wert '1'); wird von den E2E-Fixtures gesetzt. */
export const ANALYTICS_OPTOUT_KEY = 'faebill_analytics_optout';

/**
 * true, wenn dieser Browser automatisierter Testtraffic ist (navigator.webdriver,
 * von Playwright/Selenium gesetzt) oder das Opt-out-Flag gesetzt ist.
 * Muss mit dem Inline-Skript in public/index.html übereinstimmen.
 */
export function isAnalyticsBlocked(): boolean {
  try {
    if (typeof navigator !== 'undefined' && navigator.webdriver === true) return true;
    if (typeof window !== 'undefined' && window.localStorage?.getItem(ANALYTICS_OPTOUT_KEY) === '1') return true;
  } catch {
    // localStorage nicht lesbar (z. B. blockiert) -> echter Nutzer, nicht sperren
  }
  return false;
}

// Erweitere Window Interface für Analytics-Status
declare global {
  interface Window {
    analyticsLoadStatus?: {
      loaded: boolean;
      error: any;
      retryCount: number;
      maxRetries: number;
    };
    pendingAnalyticsConsent?: 'granted' | 'denied';
    fallbackAnalyticsQueue?: Array<{
      timestamp: number;
      arguments: any[];
    }>;
    enableGoogleAnalytics?: () => void;
    disableGoogleAnalytics?: () => void;
  }
}

// Google Analytics Event Typen
export type GAParamValue = string | number | boolean;

/** GA4-Ecommerce-Item (nur die von uns genutzten Felder). */
export interface GAItem {
  item_id: string;
  item_name?: string;
  price?: number;
  quantity?: number;
}

export interface GAEvent {
  event_name: string;
  event_category?: string;
  event_label?: string;
  value?: number;
  user_id?: string;
  // custom_parameters trägt beliebige Scalar-Parameter PLUS optional die
  // GA4-Ecommerce-`items`-Liste (begin_checkout/purchase).
  custom_parameters?: Record<string, GAParamValue> & { items?: GAItem[] };
}

// Vordefinierte Event-Typen für bessere Typsicherheit
export interface InvoiceCreatedEvent {
  event_name: 'invoice_created';
  event_category: 'invoice';
  invoice_type: 'zugferd' | 'xrechnung' | 'offer';
  user_subscription: 'free' | 'premium';
  value?: number;
}

/**
 * GA4-Standard-Event `sign_up` (ersetzt das nie in GA4 angekommene
 * Custom-Event `user_registration`, s. architecture-ga4-funnel.md §6.1).
 */
export interface SignUpEvent {
  event_name: 'sign_up';
  method: 'email';
}

/** GA4-Standard-Event `login`. */
export interface LoginEvent {
  event_name: 'login';
  method: 'email';
}

/**
 * GA4-Standard-Event `begin_checkout` (ersetzt Custom-Event
 * `subscription_action`). `price_id` wird zum `items:[{item_id}]` gemappt.
 */
export interface BeginCheckoutEvent {
  event_name: 'begin_checkout';
  price_id: string;
  value?: number;
  currency?: string;
  /**
   * Free→Paid-Nudge-Attribution (M-N4, architecture-nudge.md §7.5): die auslösende
   * Promotion-ID aus dem `?ref=`-Query der Abo-Seite. Optional-additiv — fehlt sie,
   * feuert `begin_checkout` unverändert (kein `nudge_ref`-Parameter). Fail-closed
   * am Ursprung (`parseNudgeRef`), daher immer eine bekannte {@link NudgePromotionId}.
   */
  nudge_ref?: NudgePromotionId;
}

/**
 * GA4-Standard-Event `purchase`. `transaction_id` = Stripe-`session_id`
 * (GA4 dedupliziert reporting-seitig darüber).
 */
export interface PurchaseEvent {
  event_name: 'purchase';
  transaction_id: string;
  value?: number;
  currency?: string;
  price_id?: string;
}

/**
 * GA4-Standard-Events `view_promotion`/`select_promotion` (Free→Paid-Nudge-
 * Telemetrie, architecture-nudge.md §7.1–§7.4). Ersetzt KEIN Custom-Event —
 * es wird kein neuer Custom-Event-Namensraum eröffnet, sondern GA4s
 * fertiger Promotion-Trichter (view → select → begin_checkout → purchase)
 * genutzt.
 */
export interface PromotionEventData {
  promotion_id: NudgePromotionId;
  /** Fläche, wenn dieselbe promotion_id an mehr als einer Stelle rendert (z. B. 'inbox_stream' | 'page'). */
  creative_slot?: string;
  /**
   * Aktueller Config-Wert des Freemium-Limits zum Zeitpunkt des Events
   * (Threshold-Agnostik, architecture-nudge.md §12) — macht Auswertungen
   * über eine spätere Limit-Änderung hinweg segmentierbar.
   */
  limit_value?: number;
  count_value?: number;
}

export interface PageViewEvent {
  event_name: 'page_view';
  page_title: string;
  page_location: string;
  page_path: string;
}

export interface UserEngagementEvent {
  event_name: 'user_engagement';
  event_category: 'engagement';
  engagement_time_msec: number;
}

// Analytics Service Klasse
class AnalyticsService {
  private isEnabled: boolean = false;
  private isInitialized: boolean = false;
  private pendingEvents: GAEvent[] = [];

  constructor() {
    // Prüfe ob Analytics bereits aktiviert wurde (z.B. durch Consent Manager)
    this.checkInitialConsentStatus();
  }

  /**
   * Prüft den initialen Consent-Status
   * Wird jetzt vollständig vom Consentmanager über Events gesteuert
   */
  private checkInitialConsentStatus(): void {
    // Der Consentmanager übernimmt die komplette Consent-Verwaltung
    // Keine manuelle Prüfung mehr nötig - Analytics wird über 
    // enableGoogleAnalytics/disableGoogleAnalytics Funktionen gesteuert
  }

  /**
   * Aktiviert Google Analytics (wird vom Consent Manager aufgerufen)
   */
  public enableAnalytics(): void {
    if (isAnalyticsBlocked()) return;
    try {
      if (typeof window !== 'undefined' && typeof window.enableGoogleAnalytics === 'function') {
        window.enableGoogleAnalytics();
        this.isEnabled = true;
        this.isInitialized = true;
        
        // Sende gespeicherte Events
        this.flushPendingEvents();
        
        // Only log once in development
        if (process.env.NODE_ENV === 'development') {
          logger.debug('Google Analytics aktiviert');
        }
      }
    } catch (error) {
      logger.error('Fehler beim Aktivieren von Google Analytics:', error);
      
      // Fallback: Markiere als aktiviert aber mit Fallback-Modus
      this.isEnabled = true;
      this.isInitialized = false;
    }
  }

  /**
   * Deaktiviert Google Analytics (wird vom Consent Manager aufgerufen)
   */
  public disableAnalytics(): void {
    try {
      if (typeof window !== 'undefined' && typeof window.disableGoogleAnalytics === 'function') {
        window.disableGoogleAnalytics();
        this.isEnabled = false;
        
        // Lösche gespeicherte Events
        this.pendingEvents = [];
        
        logger.info('Google Analytics deaktiviert');
      }
    } catch (error) {
      logger.error('Fehler beim Deaktivieren von Google Analytics:', error);
    }
  }

  /**
   * Sendet ein Event an Google Analytics
   */
  public trackEvent(event: GAEvent): void {
    if (isAnalyticsBlocked()) return;
    try {
      if (!this.isEnabled) {
        // Speichere Event für späteren Versand, falls Analytics aktiviert wird
        this.enqueue(event);
        logger.debug('Analytics-Event gespeichert (Analytics deaktiviert):', event);
        return;
      }

      // Prüfe Analytics-Load-Status für besseres Error-Handling
      if (typeof window !== 'undefined' && window.analyticsLoadStatus && !window.analyticsLoadStatus.loaded) {
        // Google Analytics noch nicht geladen - Event in Queue speichern
        this.enqueue(event);
        logger.debug('Analytics-Event in Queue gespeichert (GA lädt noch):', event);
        return;
      }

      if (!this.isInitialized || typeof window === 'undefined' || typeof window.gtag !== 'function') {
        // Fallback: Speichere in lokaler Queue
        this.enqueue(event);
        logger.warn('Google Analytics nicht verfügbar - Event in Queue gespeichert');
        return;
      }

      // Bereinige Event-Parameter
      const cleanedEvent = this.sanitizeEvent(event);
      
      // Sende Event an Google Analytics
      window.gtag('event', cleanedEvent.event_name, {
        event_category: cleanedEvent.event_category,
        event_label: cleanedEvent.event_label,
        value: cleanedEvent.value,
        ...cleanedEvent.custom_parameters
      });

      logger.debug('Analytics-Event gesendet:', cleanedEvent);
    } catch (error) {
      logger.error('Fehler beim Senden des Analytics-Events:', error);
      // Fallback: Event für späteren Retry speichern
      this.enqueue(event);
    }
  }

  /**
   * Bereinigt Event-Parameter für Google Analytics
   */
  private sanitizeEvent(event: GAEvent): GAEvent {
    const sanitized: GAEvent = {
      event_name: event.event_name.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase()
    };

    if (event.event_category) {
      sanitized.event_category = event.event_category;
    }

    if (event.event_label) {
      sanitized.event_label = event.event_label;
    }

    if (typeof event.value === 'number' && !isNaN(event.value)) {
      // 2-Dezimal-Rundung statt Integer: 9,99 € muss 9,99 bleiben (Revenue-Events).
      sanitized.value = Math.round(event.value * 100) / 100;
    }

    if (event.custom_parameters) {
      const cleaned: Record<string, GAParamValue> & { items?: GAItem[] } = {};
      Object.entries(event.custom_parameters).forEach(([key, value]) => {
        // Die GA4-Ecommerce-`items`-Liste unverändert durchreichen (kein
        // Key-Cleaning der Item-Objekte, kein Verlust der Array-Struktur).
        if (key === 'items') {
          if (Array.isArray(value)) {
            cleaned.items = value as GAItem[];
          }
          return;
        }
        const cleanKey = key.replace(/[^a-zA-Z0-9_]/g, '_').toLowerCase();
        cleaned[cleanKey] = value as GAParamValue;
      });
      sanitized.custom_parameters = cleaned;
    }

    return sanitized;
  }

  /**
   * Fügt ein Event der Pre-Consent-Queue hinzu und erzwingt die FIFO-Obergrenze
   * (ältester Eintrag wird verdrängt), damit die Queue in langen Sessions nicht
   * unbegrenzt wächst.
   */
  private enqueue(event: GAEvent): void {
    this.pendingEvents.push(event);
    if (this.pendingEvents.length > MAX_PENDING_EVENTS) {
      this.pendingEvents.shift();
    }
  }

  /**
   * Sendet gespeicherte Events nach Aktivierung von Analytics.
   * Snapshot-First (Queue vor dem Flush leeren), damit die Reihenfolge erhalten
   * bleibt und bei einem Re-Queue (GA noch nicht geladen) nichts verloren geht.
   */
  private flushPendingEvents(): void {
    if (this.pendingEvents.length === 0) {
      return;
    }
    const queued = this.pendingEvents;
    this.pendingEvents = [];
    logger.info(`Sende ${queued.length} gespeicherte Analytics-Events`);
    queued.forEach(event => {
      this.trackEvent(event);
    });
  }

  /**
   * Typisierte Methoden für spezifische Events
   */

  public trackInvoiceCreated(data: Omit<InvoiceCreatedEvent, 'event_name' | 'event_category'>): void {
    this.trackEvent({
      event_name: 'invoice_created',
      event_category: 'invoice',
      ...data
    });
  }

  /**
   * GA4-Standard-Event `sign_up` (ersetzt `trackUserRegistration`).
   * Feuert nur im Erfolgspfad der Registrierung.
   */
  public trackSignUp(data: Omit<SignUpEvent, 'event_name'>): void {
    this.trackEvent({
      event_name: 'sign_up',
      event_category: 'engagement',
      custom_parameters: { method: data.method }
    });
  }

  /**
   * GA4-Standard-Event `login`. Feuert nach erfolgreichem Credential-Login
   * (auch bei nicht verifizierter E-Mail).
   */
  public trackLogin(data: Omit<LoginEvent, 'event_name'>): void {
    this.trackEvent({
      event_name: 'login',
      event_category: 'engagement',
      custom_parameters: { method: data.method }
    });
  }

  /**
   * GA4-Standard-Event `begin_checkout` (ersetzt `trackSubscriptionAction`).
   * `price_id` wird zu `items:[{item_id}]`; `value`/`currency` optional (ohne
   * `meta` feuert das Event GA4-valide ohne Revenue). `nudge_ref` (M-N4) wird
   * nur als custom param angehängt, wenn gesetzt.
   */
  public trackBeginCheckout(data: Omit<BeginCheckoutEvent, 'event_name'>): void {
    // items per Property-Zuweisung (nicht im Objekt-Literal), sonst kollidiert
    // der GAItem[]-Wert mit der Record<string, GAParamValue>-Index-Signatur (TS2322).
    const custom_parameters: Record<string, GAParamValue> & { items?: GAItem[] } = {};
    custom_parameters.items = [{ item_id: data.price_id }];
    if (data.currency) {
      custom_parameters.currency = data.currency;
    }
    // Nudge-Attribution (M-N4): nur bei vorhandenem Wert setzen — kein leerer/
    // undefined-Parameter in GA4 (Kardinalitäts-Schutz; fail-closed am Ursprung).
    if (data.nudge_ref) {
      custom_parameters.nudge_ref = data.nudge_ref;
    }
    this.trackEvent({
      event_name: 'begin_checkout',
      event_category: 'ecommerce',
      value: data.value,
      custom_parameters
    });
  }

  /**
   * GA4-Standard-Event `purchase`. `transaction_id` (= Stripe `session_id`)
   * ist Pflicht; `value`/`currency`/`price_id` optional. Fehlt der
   * Pending-Record, feuert das Event mit `transaction_id` ohne `value`.
   */
  public trackPurchase(data: Omit<PurchaseEvent, 'event_name'>): void {
    const custom_parameters: Record<string, GAParamValue> & { items?: GAItem[] } = {
      transaction_id: data.transaction_id
    };
    if (data.currency) {
      custom_parameters.currency = data.currency;
    }
    if (data.price_id) {
      custom_parameters.items = [{ item_id: data.price_id }];
    }
    this.trackEvent({
      event_name: 'purchase',
      event_category: 'ecommerce',
      value: data.value,
      custom_parameters
    });
  }

  /**
   * Trackt eine SPA-Seitenansicht als GA4-Event `page_view`.
   * Delegiert an `trackEvent`: vor Consent wird das Event gequeued (nicht mehr
   * still verworfen wie zuvor), nach Consent-Flush als
   * `gtag('event','page_view',{ page_title, page_location, page_path })` gesendet.
   */
  public trackPageView(data: Omit<PageViewEvent, 'event_name'>): void {
    this.trackEvent({
      event_name: 'page_view',
      custom_parameters: {
        page_title: data.page_title,
        page_location: data.page_location,
        page_path: data.page_path
      }
    });
  }

  /**
   * GA4-Standard-Event `view_promotion` (Free→Paid-Nudge, architecture-nudge.md
   * §7.3/§7.4). Feuert, wenn eine Nudge-Fläche (UsageMeter/Hard-Limit-Banner/
   * FeatureTeaser/Lock-Card) sichtbar wird. Läuft vollständig über
   * `trackEvent` — erbt dessen Queue-/Consent-Semantik unverändert, KEINE
   * neue Versandlogik.
   */
  public trackViewPromotion(data: PromotionEventData): void {
    this.trackEvent(this.buildPromotionEvent('view_promotion', data));
  }

  /**
   * GA4-Standard-Event `select_promotion` (architecture-nudge.md §7.3/§7.4).
   * Feuert beim Klick auf den Nudge-Upgrade-CTA einer Fläche.
   */
  public trackSelectPromotion(data: PromotionEventData): void {
    this.trackEvent(this.buildPromotionEvent('select_promotion', data));
  }

  /**
   * Baut das gemeinsame `GAEvent` für view_promotion/select_promotion.
   * `promotion_name` ist konstant ('premium_upgrade'), da es genau einen
   * Nudge-Werbeverbund gibt — `promotion_id` differenziert die Fläche.
   */
  private buildPromotionEvent(
    eventName: 'view_promotion' | 'select_promotion',
    data: PromotionEventData
  ): GAEvent {
    const custom_parameters: Record<string, GAParamValue> = {
      promotion_id: data.promotion_id,
      promotion_name: 'premium_upgrade'
    };

    if (data.creative_slot) {
      custom_parameters.creative_slot = data.creative_slot;
    }
    if (typeof data.limit_value === 'number' && !isNaN(data.limit_value)) {
      custom_parameters.limit_value = data.limit_value;
    }
    if (typeof data.count_value === 'number' && !isNaN(data.count_value)) {
      custom_parameters.count_value = data.count_value;
    }

    return {
      event_name: eventName,
      event_category: 'ecommerce',
      custom_parameters
    };
  }

  /**
   * Status-Getter
   */
  public get enabled(): boolean {
    return this.isEnabled;
  }

  public get initialized(): boolean {
    return this.isInitialized;
  }

  public get pendingEventCount(): number {
    return this.pendingEvents.length;
  }
}

// Singleton-Instanz
const analyticsService = new AnalyticsService();

export default analyticsService;

// Type Declarations für globale gtag-Funktion
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    enableGoogleAnalytics?: () => void;
    disableGoogleAnalytics?: () => void;
  }
}
